import type {BaseFilterKeys} from '@igp/shared/api';
import type {DateRangeFilterValue} from '@igp/shared/components/filter';
import type {CountryLevelReport as CountryLevelReportServerModel, CountryLevelReportPage, Timestamp} from '@igp/shared/contract/pam';
import type {Path} from '@igp/shared/utils';

export const CountryLevelPartnerReportServiceTypes = {
    downloadClientStrategy: Symbol('CountryLevelPartnerReportDownloadClientStrategy'),
    downloadServerStrategy: Symbol('CountryLevelPartnerReportDownloadServerStrategy'),
    service: Symbol('CountryLevelPartnerReportService'),
} as const;

export const summaryId = 'Total';

export type CountryLevelReport = CountryLevelReportServerModel & {
    is_summary?: boolean;
};

export type CountryLevelPartnerReportQueryKeys = Path<CountryLevelReportPage>;

export type CountryLevelPartnerReportDownloadFields = Path<CountryLevelReport>;

export type CountryLevelPartnerReportQueryFilterKeys = BaseFilterKeys | 'country' | 'date_from' | 'date_to' | 'date_min';

export type CountryLevelPartnerReportFilterViewModel = {
    countries: string[];
    date: DateRangeFilterValue;
};

export type CountryLevelPartnerReportFilterName = keyof CountryLevelPartnerReportFilterViewModel;

export type CountryLevelPartnerReportViewModel = {
    casino_ggr: number;
    country: string;
    daily_active_users: number;
    date_ts: Timestamp;
    deposit_count: number;
    deposit_users: number;
    first_deposit_amount: number;
    first_time_deposit_users: number;
    global_spins_ggr: number;
    net_deposit: number;
    new_users: number;
    payout_amount: number;
    poker_cash_ggr: number;
    poker_mtt_ggr: number;
    promotional_spend: number;
    retained_deposit_amount: number;
    retained_deposit_users: number;
    sports_ggr: number;
    total_bet_amount: number;
    total_deposit_amount: number;
    total_ggr: number;
    total_hands: number;
    total_mtt_entries: number;
    total_withdrawal_amount: number;
    withdrawal_count: number;
    withdrawal_users: number;
};

export type CountryLevelPartnerReportViewModelKeys = keyof CountryLevelPartnerReportViewModel;

export class CountryLevelPartnerReportDownloadModel implements Record<CountryLevelPartnerReportViewModelKeys, string> {
    casino_ggr: string;
    country: string;
    daily_active_users: string;
    date_ts: string;
    deposit_count: string;
    deposit_users: string;
    first_deposit_amount: string;
    first_time_deposit_users: string;
    global_spins_ggr: string;
    net_deposit: string;
    new_users: string;
    payout_amount: string;
    poker_cash_ggr: string;
    poker_mtt_ggr: string;
    promotional_spend: string;
    retained_deposit_amount: string;
    retained_deposit_users: string;
    sports_ggr: string;
    total_bet_amount: string;
    total_deposit_amount: string;
    total_ggr: string;
    total_hands: string;
    total_mtt_entries: string;
    total_withdrawal_amount: string;
    withdrawal_count: string;
    withdrawal_users: string;
}

export type CountryLevelPartnerReportColumnSettings = Record<CountryLevelPartnerReportViewModelKeys, null>;

export const allColumns = [
    'casino_ggr',
    'country',
    'daily_active_users',
    'date_ts',
    'deposit_count',
    'deposit_users',
    'first_deposit_amount',
    'first_time_deposit_users',
    'global_spins_ggr',
    'net_deposit',
    'new_users',
    'payout_amount',
    'poker_cash_ggr',
    'poker_mtt_ggr',
    'promotional_spend',
    'retained_deposit_amount',
    'retained_deposit_users',
    'sports_ggr',
    'total_bet_amount',
    'total_deposit_amount',
    'total_ggr',
    'total_hands',
    'total_mtt_entries',
    'total_withdrawal_amount',
    'withdrawal_count',
    'withdrawal_users',
] as const;

export type CountryLevelPartnerReportColumnSettingsKeys = (typeof allColumns)[number];

export type CountryLevelPartnerReportSortKeys = keyof Omit<CountryLevelReportServerModel, '__typename'>;
